import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { tenantsApi } from '@/lib/api';
import { ChatCircle, MagnifyingGlass } from '@phosphor-icons/react';

const DEFAULT_TEMPLATES = {
  ticket_submitted_customer: "✅ *Service Request Received*\n\nDear {customer_name},\n\nYour service request has been registered successfully.\n\n📋 *Ticket ID:* {ticket_number}\n📦 *Product:* {product_name}\n\nOur team will review your request and assign a technician shortly. You will receive updates at each stage.",
  ticket_submitted_agent: "🔔 *New Service Request*\n\nHello {agent_name},\n\nA new ticket requires your attention.\n\n📋 *Ticket:* {ticket_number}\n📦 *Product:* {product_name}\n⚡ *Priority:* {priority}\n\nPlease review and process at the earliest.",
  ticket_picked_up_customer: "👀 *Ticket Under Review*\n\nDear {customer_name},\n\nYour ticket *{ticket_number}* for *{product_name}* has been picked up for review by *{agent_name}*.\n\nWe'll notify you once it's approved and a technician is assigned.",
  ticket_approved_customer: "✅ *Ticket Approved*\n\nDear {customer_name},\n\nYour ticket *{ticket_number}* for *{product_name}* has been approved.\n\nA technician will be assigned to your request shortly. You will receive their details once confirmed.",
  ticket_rejected_customer: "❌ *Service Request Update*\n\nDear {customer_name},\n\nWe regret to inform you that your ticket *{ticket_number}* for *{product_name}* could not be approved at this time.\n\n📝 *Reason:* {reason}\n\nIf you believe this is an error, please contact our support team for further assistance.",
  tech_assigned_customer: "🔧 *Technician Assigned*\n\nDear {customer_name},\n\nA technician has been assigned to your ticket *{ticket_number}* for *{product_name}*.\n\n👤 *Technician:* {tech_name}\n📞 *Contact:* {tech_phone}",
  tech_reassigned_customer: "🔧 *Technician Reassigned*\n\nDear {customer_name},\n\nA technician has been reassigned to your ticket *{ticket_number}* for *{product_name}*.\n\n👤 *Technician:* {tech_name}\n📞 *Contact:* {tech_phone}",
  tech_assigned_tech: "📋 *New Job Assignment*\n\nHello {tech_name},\n\nYou have been assigned a new service job.\n\n🎫 *Ticket:* {ticket_number}\n👤 *Customer:* {customer_name}\n📞 *Contact:* {customer_phone}\n📍 *Address:* {customer_address}\n\nPlease proceed to the customer's location at the earliest.",
  job_completed_customer: "✅ *Service Completed*\n\nDear {customer_name},\n\nYour service job for ticket *{ticket_number}* ({product_name}) has been completed successfully.\n\nThank you for choosing {brand_name}. We hope you had a great experience!",
  sla_breached_support: "🚨 *SLA Breach Alert*\n\nTicket *{ticket_number}* has breached its *{breach_type}* SLA target.\n⚡ *Priority:* {priority}\n\nImmediate action is required.",
  sla_breached_admin: "🚨 *SLA Breach Alert*\n\nHello {admin_name},\n\nTicket *{ticket_number}* ({priority} priority) has breached its *{breach_type}* SLA target.\n\nPlease investigate and take corrective action immediately."
};

const TEMPLATE_METADATA = [
  {
    key: 'ticket_submitted_customer',
    label: 'Customer: Ticket Submitted',
    description: 'Sent to the customer when their service ticket is successfully submitted.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}']
  },
  {
    key: 'ticket_submitted_agent',
    label: 'Agent: Ticket Submitted',
    description: 'Sent to all active support agents when a new ticket is submitted.',
    variables: ['{agent_name}', '{ticket_number}', '{product_name}', '{priority}']
  },
  {
    key: 'ticket_picked_up_customer',
    label: 'Customer: Ticket Under Review',
    description: 'Sent to the customer when an agent picks up the ticket for review.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}', '{agent_name}']
  },
  {
    key: 'ticket_approved_customer',
    label: 'Customer: Ticket Approved',
    description: 'Sent to the customer when the ticket is approved and ready for technician assignment.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}']
  },
  {
    key: 'ticket_rejected_customer',
    label: 'Customer: Ticket Rejected',
    description: 'Sent to the customer if the ticket is rejected, specifying the rejection reason.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}', '{reason}']
  },
  {
    key: 'tech_assigned_customer',
    label: 'Customer: Technician Assigned',
    description: 'Sent to the customer when a technician is assigned to the service request.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}', '{tech_name}', '{tech_phone}', '{close_otp}']
  },
  {
    key: 'tech_reassigned_customer',
    label: 'Customer: Technician Reassigned',
    description: 'Sent to the customer if the technician is reassigned to another technician.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}', '{tech_name}', '{tech_phone}', '{close_otp}']
  },
  {
    key: 'tech_assigned_tech',
    label: 'Technician: Job Assigned',
    description: 'Sent to the assigned technician containing customer contact and address details.',
    variables: ['{tech_name}', '{ticket_number}', '{customer_name}', '{customer_phone}', '{customer_address}']
  },
  {
    key: 'job_completed_customer',
    label: 'Customer: Service Completed',
    description: 'Sent to the customer when the job is marked complete, including a rating link if portal is configured.',
    variables: ['{customer_name}', '{ticket_number}', '{product_name}', '{brand_name}', '{rating_link}']
  },
  {
    key: 'sla_breached_support',
    label: 'Support: SLA Breached',
    description: 'Sent to the support phone number when a ticket breaches an SLA target.',
    variables: ['{ticket_number}', '{breach_type}', '{priority}']
  },
  {
    key: 'sla_breached_admin',
    label: 'Admin: SLA Breached',
    description: 'Sent to all super administrators when a ticket breaches an SLA target.',
    variables: ['{admin_name}', '{ticket_number}', '{breach_type}', '{priority}']
  }
];

const getRecipientGroup = (key) => {
  if (key.includes('customer')) return 'Customer';
  if (key.includes('tech')) return 'Technician';
  if (key.includes('agent') || key.includes('support')) return 'Agent & Support';
  if (key.includes('admin')) return 'Admin';
  return 'Other';
};

const renderTemplatePreview = (templateText) => {
  if (!templateText) return '';
  let preview = templateText;
  
  const dummyValues = {
    '{customer_name}': 'John Doe',
    '{ticket_number}': 'T-1082',
    '{product_name}': 'Kawaki Cooler Pro X',
    '{agent_name}': 'Sarah Connor',
    '{priority}': 'High',
    '{reason}': 'Under warranty, compressor replacement required',
    '{tech_name}': 'Robert Patrick',
    '{tech_phone}': '+91 98765 43210',
    '{close_otp}': '5829',
    '{brand_name}': 'Kawaki Coolers',
    '{rating_link}': 'https://resolv.io/rate/T-1082',
    '{customer_phone}': '+91 99999 88888',
    '{customer_address}': '123 Tech Park, Sector 4, Bangalore, 560001',
    '{breach_type}': 'Response SLA',
    '{admin_name}': 'David Sachdev'
  };

  Object.entries(dummyValues).forEach(([placeholder, value]) => {
    const escapedPlaceholder = placeholder.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    preview = preview.replace(new RegExp(escapedPlaceholder, 'g'), value);
  });

  // Simple WhatsApp markdown parsing (just *bold* to <strong>bold</strong>)
  preview = preview
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  preview = preview.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');

  return <span dangerouslySetInnerHTML={{ __html: preview }} />;
};

export const WhatsAppTemplatesForm = ({ tenantConfig, setTenantConfig }) => {
  const [whatsappTemplates, setWhatsappTemplates] = useState({});
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [editingTemplateKey, setEditingTemplateKey] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [localTemplateText, setLocalTemplateText] = useState('');
  const textareaRef = useRef(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (tenantConfig) {
      const customTemplates = tenantConfig.whatsapp_templates || {};
      const mergedTemplates = {};
      Object.keys(DEFAULT_TEMPLATES).forEach(key => {
        mergedTemplates[key] = customTemplates[key] !== undefined ? customTemplates[key] : DEFAULT_TEMPLATES[key];
      });
      setWhatsappTemplates(mergedTemplates);
    }
  }, [tenantConfig]);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const editingMeta = TEMPLATE_METADATA.find(m => m.key === editingTemplateKey);

  const filteredTemplates = TEMPLATE_METADATA.filter(meta => {
    const matchesSearch = 
      meta.label.toLowerCase().includes(templateSearch.toLowerCase()) || 
      meta.description.toLowerCase().includes(templateSearch.toLowerCase());
      
    let matchesRecipient = true;
    if (recipientFilter !== 'all') {
      const group = getRecipientGroup(meta.key).toLowerCase();
      if (recipientFilter === 'customer') matchesRecipient = group === 'customer';
      else if (recipientFilter === 'tech') matchesRecipient = group === 'technician';
      else if (recipientFilter === 'agent') matchesRecipient = group === 'agent & support';
      else if (recipientFilter === 'admin') matchesRecipient = group === 'admin';
    }
    
    let matchesStatus = true;
    if (statusFilter !== 'all') {
      const isCustomized = (whatsappTemplates[meta.key] || '') !== DEFAULT_TEMPLATES[meta.key];
      if (statusFilter === 'customized') matchesStatus = isCustomized;
      else if (statusFilter === 'default') matchesStatus = !isCustomized;
    }
    
    return matchesSearch && matchesRecipient && matchesStatus;
  });

  const handleSaveWhatsappTemplates = async (e) => {
    if (e) e.preventDefault();
    if (!tenantConfig?.id) {
      toast.error('Tenant configuration context not loaded');
      return;
    }
    
    setSavingWhatsapp(true);
    try {
      const { data } = await tenantsApi.update(tenantConfig.id, {
        whatsapp_templates: whatsappTemplates
      });
      
      setTenantConfig(data);
      toast.success('WhatsApp notification templates updated successfully!');
    } catch (error) {
      console.error('Failed to update WhatsApp templates:', error);
      toast.error(error.response?.data?.detail || 'Failed to update templates');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleOpenEditModal = (key) => {
    setEditingTemplateKey(key);
    setLocalTemplateText(whatsappTemplates[key] || '');
    setShowTemplateModal(true);
  };

  const handleApplyTemplateChanges = () => {
    if (!editingTemplateKey) return;
    setWhatsappTemplates(prev => ({
      ...prev,
      [editingTemplateKey]: localTemplateText
    }));
    setShowTemplateModal(false);
    toast.success('Changes applied locally (save changes to persist to database)');
  };

  const handleResetLocalTemplate = () => {
    if (editingTemplateKey) {
      setLocalTemplateText(DEFAULT_TEMPLATES[editingTemplateKey] || '');
      toast.info('Template draft reset to system default');
    }
  };

  const handleInsertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setLocalTemplateText(prev => prev + variable);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);

    setLocalTemplateText(before + variable + after);

    // Put focus back and move cursor after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-sm text-primary">
              <ChatCircle size={24} style={{ color: brandColorAccent }} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-850 tracking-tight">WhatsApp Notification Templates</h3>
              <p className="text-slate-500 text-xs mt-0.5">
                Configure custom messages sent at different stages of ticket execution. Use standard markdown placeholders.
              </p>
            </div>
          </div>
          <Button
            type="button"
            onClick={handleSaveWhatsappTemplates}
            disabled={savingWhatsapp}
            className="font-bold text-xs uppercase tracking-wider text-white"
            style={{ backgroundColor: brandColorAccent }}
          >
            {savingWhatsapp ? 'Saving...' : 'Save Templates'}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-6 relative">
              <MagnifyingGlass 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                size={16} 
              />
              <input
                type="text"
                placeholder="Search templates by name or description..."
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 h-10 border border-slate-300 bg-white rounded-md text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="md:col-span-3">
              <Select value={recipientFilter} onValueChange={setRecipientFilter}>
                <SelectTrigger className="h-10 border-slate-300 text-slate-900 bg-white text-xs font-semibold">
                  <SelectValue placeholder="All Recipients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Recipients</SelectItem>
                  <SelectItem value="customer">Customers Only</SelectItem>
                  <SelectItem value="tech">Technicians Only</SelectItem>
                  <SelectItem value="agent">Agents & Support</SelectItem>
                  <SelectItem value="admin">Admins Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 border-slate-300 text-slate-900 bg-white text-xs font-semibold">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="customized">Customized</SelectItem>
                  <SelectItem value="default">System Default</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                    <th className="py-3 px-4">Template Name</th>
                    <th className="py-3 px-4">Description</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                  {filteredTemplates.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-slate-400 font-semibold italic">
                        No templates match your filter criteria
                      </td>
                    </tr>
                  ) : (
                    filteredTemplates.map((meta) => {
                      const isCustomized = (whatsappTemplates[meta.key] || '') !== DEFAULT_TEMPLATES[meta.key];
                      return (
                        <tr key={meta.key} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-3.5 px-4 font-bold text-slate-900">
                            {meta.label}
                          </td>
                          <td className="py-3.5 px-4 text-slate-500 max-w-xs md:max-w-md truncate" title={meta.description}>
                            {meta.description}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                              isCustomized
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-slate-100 text-slate-600 border-slate-200'
                            }`}>
                              {isCustomized ? 'Customized' : 'System Default'}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEditModal(meta.key)}
                              className="font-bold text-xs uppercase tracking-wider text-primary"
                              style={{ color: brandColorAccent }}
                            >
                              View & Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-slate-100">
            <span className="text-xs text-slate-500 font-semibold">
              Showing {filteredTemplates.length} of {TEMPLATE_METADATA.length} templates
            </span>
            <Button
              type="button"
              onClick={handleSaveWhatsappTemplates}
              disabled={savingWhatsapp}
              className="font-bold text-xs uppercase tracking-wider text-white px-6 h-10 shadow-sm"
              style={{ backgroundColor: brandColorAccent }}
            >
              {savingWhatsapp ? 'Saving...' : 'Save Templates'}
            </Button>
          </div>
        </div>
      </div>

      {/* View & Edit Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-[850px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-slate-800 tracking-tight">
              Edit Template: {editingMeta?.label}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs mt-1">
              {editingMeta?.description}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 my-4">
            {/* Left Column - Editor */}
            <div className="md:col-span-7 space-y-4 flex flex-col justify-between">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700">Template Text</label>
                <textarea
                  ref={textareaRef}
                  value={localTemplateText}
                  onChange={(e) => setLocalTemplateText(e.target.value)}
                  rows={9}
                  className="w-full rounded-md border border-slate-300 bg-white p-3 text-sm font-medium text-slate-800 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none font-mono"
                  placeholder="Enter template text..."
                />
              </div>

              {editingMeta?.variables && editingMeta.variables.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-black uppercase text-slate-400">Available Placeholders (click to insert at cursor)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {editingMeta.variables.map((v) => (
                      <code
                        key={v}
                        onClick={() => handleInsertVariable(v)}
                        className="px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 border border-slate-350 text-slate-700 font-mono text-[10px] rounded cursor-pointer transition-colors"
                        title="Click to insert at cursor"
                      >
                        {v}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Live WhatsApp Preview */}
            <div className="md:col-span-5 flex flex-col">
              <label className="text-xs font-bold text-slate-700 mb-1.5">WhatsApp Chat Preview</label>
              <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden bg-[#efeae2] flex flex-col p-4 min-h-[260px] relative shadow-inner">
                {/* Background watermark simulated dots */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #000 10%, transparent 11%)', backgroundSize: '12px 12px' }}></div>
                
                {/* Message bubble */}
                <div className="space-y-2 flex-1 overflow-y-auto relative z-10">
                  <div className="bg-[#dcf8c6] ml-auto p-3 rounded-lg shadow-sm text-xs font-normal text-slate-800 leading-relaxed max-w-[90%] border border-emerald-100 whitespace-pre-wrap font-sans relative">
                    {renderTemplatePreview(localTemplateText)}
                    <div className="text-[9px] text-[#5c6870] text-right mt-1 flex items-center justify-end gap-1">
                      <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="text-[#34b7f1] font-bold">✓✓</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-2 border-t border-slate-300/45 flex items-center gap-1.5 text-[9px] text-slate-400 font-bold justify-center relative z-10 select-none">
                  <span>WhatsApp Dynamic Simulation</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-slate-100">
            <Button
              type="button"
              variant="ghost"
              onClick={handleResetLocalTemplate}
              className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-red-600 hover:bg-red-50 h-9 px-3"
            >
              Reset to Default
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTemplateModal(false)}
                className="text-xs font-bold uppercase tracking-wider h-9 px-4"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleApplyTemplateChanges}
                className="text-xs font-bold uppercase tracking-wider text-white h-9 px-4"
                style={{ backgroundColor: brandColorAccent }}
              >
                Apply Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
